import type { HttpHandler } from 'msw'
import { getDiffusionsErrorsHandler } from '~/msw/handlers/overrides/getDiffusionsHandler'
import { getPropertyArchivedHandler } from '~/msw/handlers/overrides/getPropertyArchivedHandler'
import { getUserOrganizationsSansOfficeHandler } from '~/msw/handlers/overrides/getUserOrganizationsHandler'
import { publishDiffusionForProductErrorHandler } from '~/msw/handlers/overrides/publishDiffusionForProductHandler'
import { relaunchDiffusionForProductErrorHandler } from '~/msw/handlers/overrides/relaunchDiffusionForProductHandler'
import { relaunchDiffusionErrorHandler } from '~/msw/handlers/overrides/relaunchDiffusionHandler'
import { unpublishDiffusionForProductErrorHandler } from '~/msw/handlers/overrides/unpublishDiffusionForProductHandler'
import { updateProduitOrganisationErrorHandler } from '~/msw/handlers/overrides/updateProduitOrganisationHandler'

/**
 * Registre des « dynamic mock scenarios » MSW, **groupés par page** (pathname).
 *
 * Le worker (cf. ~/msw/worker.ts) lit `window.location.pathname` + le query param
 * `?scenario=...` au démarrage et préfixe le jeu de handlers correspondant aux
 * handlers de base (le plus à gauche gagne côté MSW). Grouper par page rend la
 * relation page ↔ scénario explicite : un scénario n'est valable (typé) et n'a
 * d'effet que sur la page à laquelle il appartient.
 *
 * L'implémentation des handlers vit à côté du handler de base de l'endpoint
 * (ex. `getDiffusionsErrorsHandler`) ; ici on ne fait que les composer.
 *
 * @see https://mswjs.io/docs/best-practices/dynamic-mock-scenarios
 */
export const scenariosByPath = {
  '/diffusion': {
    // Annonces déjà en erreur au chargement du listing.
    errors: [getDiffusionsErrorsHandler()],
    // Les endpoints « relancer »/« publier » échouent : permet de tester le toast
    // d'erreur « La publication a échoué » après une tentative de publication.
    relaunchError: [
      relaunchDiffusionErrorHandler(),
      relaunchDiffusionForProductErrorHandler(),
      publishDiffusionForProductErrorHandler(),
    ],
    // L'endpoint « depublier » échoue : permet de tester le toast d'erreur
    // « La dépublication a échoué » après une tentative de dépublication.
    unpublishError: [
      unpublishDiffusionForProductErrorHandler(),
    ],
    sansOffice: [getUserOrganizationsSansOfficeHandler()],
  },
  '/annonces/2': {
    // Le bien est archivé (vendu) : aucune transition proposée, le badge de sous-statut
    // n'ouvre plus de menu et annonce un statut définitif.
    archived: [getPropertyArchivedHandler()],
  },
  '/options': {
    // La mise à jour de l'identifiant diffuseur échoue : permet de tester le toast
    // d'erreur « La mise à jour a échoué » et la restauration de la valeur précédente.
    updateError: [updateProduitOrganisationErrorHandler()],
  },
} satisfies Record<string, Record<string, HttpHandler[]>>

/** Chemins disposant d'au moins un scénario. */
export type ScenarioPath = keyof typeof scenariosByPath

/** Noms de scénarios valides pour un chemin donné (dérivés du registre). */
export type ScenarioFor<P extends ScenarioPath> = keyof (typeof scenariosByPath)[P] & string

/** Handlers à appliquer pour le couple (pathname, scenario) ; `[]` si inexistant. */
export function resolveScenarioHandlers(pathname: string, scenario: string | null): HttpHandler[] {
  if (!scenario)
    return []
  const byName = (scenariosByPath as Record<string, Record<string, HttpHandler[]>>)[pathname]
  return byName?.[scenario] ?? []
}
